import type { FlowStep } from '@forq/ui';
import {
  allowedTransitionsFor,
  orderPhaseLabels,
  orderPhaseOf,
  orderPhaseRank,
  type OrderPhase,
  type OrderProfile,
  type OrderRecord,
  type OrderStatus,
} from '@forq/orders-data';

/**
 * Status-Label eines Vorgangs = das Label seiner groben ARISTA-Phase (Etappe 3). Mehrere Feinstatus
 * teilen sich ein Label (in-progress/reported/billing-ready = „In Arbeit", draft/new = „Offen"),
 * damit der Vorgang beleggetrieben in wenigen Phasen läuft statt in einer langen Statuskette.
 */
export const statusLabels: Record<OrderStatus, string> = {
  draft: orderPhaseLabels.open,
  new: orderPhaseLabels.open,
  planned: orderPhaseLabels.planned,
  assigned: orderPhaseLabels.assigned,
  'in-progress': orderPhaseLabels['in-progress'],
  reported: orderPhaseLabels['in-progress'],
  'billing-ready': orderPhaseLabels['in-progress'],
  billed: orderPhaseLabels.billed,
  completed: orderPhaseLabels.completed,
  cancelled: orderPhaseLabels.cancelled,
};

/**
 * Die Phasen, die jedes Profil im Status-Trail sichtbar macht -- auch wenn noch nicht erreicht. Der
 * tatsächliche aktuelle Status und die Historie werden immer eingemischt, sodass der Trail nie eine
 * Phase auslässt, in der der Vorgang wirklich ist (oder war). Nur Service behält Geplant/Zugewiesen.
 */
const profilePhaseMilestones: Record<OrderProfile, readonly OrderPhase[]> = {
  general: ['open', 'in-progress', 'billed', 'completed'],
  service: ['open', 'planned', 'assigned', 'in-progress', 'billed', 'completed'],
  internal: ['open', 'in-progress', 'completed'],
  sales: ['open', 'in-progress', 'billed', 'completed'],
  rental: ['open', 'planned', 'in-progress', 'billed', 'completed'],
  'spare-parts': ['open', 'in-progress', 'billed', 'completed'],
  accessories: ['open', 'in-progress', 'billed', 'completed'],
  'trade-in': ['open', 'in-progress', 'completed'],
};

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso));
}

export interface FlowTransition {
  readonly toStatus: OrderStatus;
  readonly label: string;
  readonly permissions: readonly string[];
  readonly tone: 'default' | 'danger';
}

/**
 * Permissions accepted to move a Vorgang INTO the given status — mirrors the forq.order_transition
 * RPC exactly (checked in the UI here, enforced again in the database, so the two never disagree):
 * billing-ready needs orders.send_to_billing, completed needs orders.complete, every other step
 * needs orders.edit OR orders.assign.
 */
const transitionPermissions: Partial<Record<OrderStatus, readonly string[]>> = {
  'billing-ready': ['orders.send_to_billing'],
  completed: ['orders.complete'],
};

function transitionPermissionsFor(toStatus: OrderStatus): readonly string[] {
  return transitionPermissions[toStatus] ?? ['orders.edit', 'orders.assign'];
}

/**
 * Grobe Phasen-Übergänge, die im Vorgang als „WEITER ZU" angeboten werden. Der Feinstatus dahinter
 * ist der Repräsentant der Zielphase. Abrechnen ist KEIN Status-Knopf mehr -- es passiert über die
 * Rechnung im Beleg-Aktions-Center (beleggetrieben). Ein bereits abgerechneter/abgeschlossener
 * Vorgang wird nur über Storno/Gutschrift wieder geöffnet, nicht per Knopf hier.
 */
function coarseTargetsFor(order: OrderRecord): readonly OrderStatus[] {
  const isService = order.profile === 'service';
  let ideal: readonly OrderStatus[];
  switch (orderPhaseOf(order.status)) {
    case 'open': ideal = isService ? ['planned', 'assigned', 'in-progress', 'cancelled'] : ['in-progress', 'cancelled']; break;
    case 'planned': ideal = ['assigned', 'in-progress', 'cancelled']; break;
    case 'assigned': ideal = ['in-progress', 'cancelled']; break;
    case 'in-progress': ideal = ['cancelled']; break;
    case 'billed': ideal = ['completed']; break;
    default: ideal = []; // completed / cancelled: Reopen läuft über Gutschrift, nicht über einen Knopf
  }
  // Nie einen Übergang anbieten, den der Statusgraph vom aktuellen Feinstatus nicht zulässt.
  const allowed = allowedTransitionsFor(order.status);
  return ideal.filter((target) => allowed.includes(target));
}

/** The coarse phase steps the user is permitted to perform from the order's current phase. */
export function orderFlowTransitions(order: OrderRecord, permissions: ReadonlySet<string>): readonly FlowTransition[] {
  return coarseTargetsFor(order)
    .map((toStatus) => ({
      toStatus,
      label: orderPhaseLabels[orderPhaseOf(toStatus)],
      permissions: transitionPermissionsFor(toStatus),
      tone: toStatus === 'cancelled' ? ('danger' as const) : ('default' as const),
    }))
    .filter((transition) => transition.permissions.some((permission) => permissions.has(permission)));
}

/**
 * Builds the FORQ Flow status trail for a Vorgang at PHASE granularity (Etappe 3): Offen → In Arbeit →
 * Abgerechnet → Abgeschlossen, Service zusätzlich Geplant/Zugewiesen. Phasen vor der aktuellen sind
 * done, die aktuelle current, spätere upcoming. Ein stornierter Vorgang zeigt die erreichten Phasen
 * plus einen terminalen Storniert-Schritt.
 */
export function buildOrderFlow(order: OrderRecord): readonly FlowStep[] {
  const currentPhase = orderPhaseOf(order.status);

  // Meilenstein-Datum je Phase aus der Historie: erstes Vorkommen eines Status dieser Phase.
  const metaByPhase = new Map<OrderPhase, string>();
  for (const entry of order.statusHistory) {
    const phase = orderPhaseOf(entry.toStatus);
    if (!metaByPhase.has(phase)) metaByPhase.set(phase, `${formatWhen(entry.occurredAt)} · ${entry.actor}`);
  }
  const reached = new Set<OrderPhase>(order.statusHistory.map((entry) => orderPhaseOf(entry.toStatus)));

  const shown = new Set<OrderPhase>(profilePhaseMilestones[order.profile]);
  reached.forEach((phase) => { if (phase !== 'cancelled') shown.add(phase); });
  if (currentPhase !== 'cancelled') shown.add(currentPhase);

  const ordered = [...shown].sort((left, right) => orderPhaseRank[left] - orderPhaseRank[right]);

  if (currentPhase === 'cancelled') {
    const steps: FlowStep[] = ordered.map((phase) => ({
      key: phase,
      label: orderPhaseLabels[phase],
      state: reached.has(phase) ? 'done' : 'skipped',
      meta: metaByPhase.get(phase),
    }));
    steps.push({ key: 'cancelled', label: orderPhaseLabels.cancelled, state: 'cancelled', meta: metaByPhase.get('cancelled') });
    return steps;
  }

  const currentRank = orderPhaseRank[currentPhase];
  return ordered.map((phase) => {
    const phaseRankValue = orderPhaseRank[phase];
    const state: FlowStep['state'] = phaseRankValue < currentRank ? 'done' : phaseRankValue === currentRank ? 'current' : 'upcoming';
    return {
      key: phase,
      label: orderPhaseLabels[phase],
      state,
      meta: phaseRankValue <= currentRank ? metaByPhase.get(phase) : undefined,
    };
  });
}
